import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Minus, Download } from "lucide-react";
import { api, fmt, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PageHeader, Card, Btn, inp } from "@/components/ui-kit";
import { MarginsCard } from "@/components/MarginsCard";
import { useAuth } from "@/context/AuthContext";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

const COLORS = ["#5aa469", "#0ea5e9", "#f59e0b", "#f43f5e", "#8b5cf6", "#14b8a6"];

function Change({ v }) {
  if (v === null || v === undefined) return <span className="text-xs text-slate-400">—</span>;
  const I = v > 0 ? TrendingUp : v < 0 ? TrendingDown : Minus;
  const c = v > 0 ? "text-emerald-700 bg-emerald-50" : v < 0 ? "text-rose-700 bg-rose-50" : "text-slate-600 bg-slate-100";
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${c}`}><I size={12} />{v > 0 ? "+" : ""}{v}%</span>;
}

export default function MonthlyReport() {
  const { restaurant } = useAuth();
  const { t, lang } = useT();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const pdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/reports/monthly.pdf", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `report_mensile_${month}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  useEffect(() => { api.get("/reports/monthly", { params: { month } }).then((x) => setR(x.data)); }, [month]);
  const shift = (d) => { const [y, m] = month.split("-").map(Number); const x = new Date(y, m - 1 + d, 1); setMonth(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`); };
  const label = (m) => new Date(`${m}-01`).toLocaleDateString(lang === "en" ? "en-GB" : "it-IT", { month: "long", year: "numeric" });
  if (!r) return null;
  return (
    <div>
      <PageHeader title={t("nav_monthly")}>
        <Btn variant="outline" onClick={() => shift(-1)}>‹</Btn>
        <input data-testid="monthly-month-input" type="month" className={inp + " w-auto"} value={month} onChange={(e) => setMonth(e.target.value)} />
        <Btn variant="outline" onClick={() => shift(1)}>›</Btn>
        <Btn data-testid="monthly-pdf-btn" disabled={busy} onClick={pdf}><Download size={16} />{t("export_pdf")}</Btn>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl bg-slate-900 p-5 text-white shadow-sm">
          <p className="text-xs font-semibold uppercase text-slate-400">{t("month_total")} · {label(r.month)}</p>
          <p data-testid="monthly-grand-total" className="mt-2 font-heading text-4xl font-extrabold text-white">{fmt(r.grand_total)}</p>
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-400"><Change v={r.grand_change_pct} /> vs {label(r.prev_month)} ({fmt(r.grand_prev_total)})</div>
        </div>
        {[["receipts_today", r.grand_receipts], ["covers", r.grand_covers], ["avg_ticket", fmt(r.grand_receipts ? r.grand_total / r.grand_receipts : 0)]].map(([k, v]) => (
          <Card key={k}><p className="text-xs font-semibold uppercase text-slate-500">{t(k)}</p><p className="mt-2 font-heading text-3xl font-extrabold">{v}</p></Card>))}
      </div>
      <Card className="mt-4">
        <p className="mb-3 font-heading font-bold">{t("revenue_trend")}</p>
        <div className="h-72">
          <ResponsiveContainer><LineChart data={r.daily}><CartesianGrid stroke="#f1f5f9" vertical={false} /><XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} fontSize={12} /><YAxis fontSize={12} tickFormatter={(v) => `${v}€`} width={60} /><Tooltip formatter={(v) => fmt(v)} labelFormatter={(d) => d} /><Legend />
            {r.restaurants.map((x, i) => <Line key={x.restaurant_id} type="monotone" dataKey={x.restaurant_id} name={x.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={false} />)}</LineChart></ResponsiveContainer>
        </div>
      </Card>
      <Card className="mt-4">
        <p className="mb-3 font-heading font-bold">{t("restaurant_comparison")}</p>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2">{t("restaurant_name")}</th><th className="text-right">{t("total")}</th><th className="text-right">{t("vs_prev")}</th><th className="text-right">{t("receipts_today")}</th><th className="text-right">{t("covers")}</th><th className="text-right">{t("avg_ticket")}</th><th className="text-right">{t("cash")}</th><th className="text-right pr-4">{t("card")}</th><th className="pr-4">{t("best_day")}</th><th>{t("top_items")}</th></tr></thead>
          <tbody className="divide-y divide-slate-100">{r.restaurants.map((x, i) => (
            <tr key={x.restaurant_id} data-testid={`monthly-row-${x.name}`}>
              <td className="py-3 font-semibold"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{x.name}</td>
              <td className="text-right font-heading font-extrabold">{fmt(x.total)}</td>
              <td className="text-right"><Change v={x.change_pct} /><p className="text-[11px] text-slate-400">{fmt(x.prev_total)}</p></td>
              <td className="text-right">{x.receipts}</td><td className="text-right">{x.covers}</td><td className="text-right">{fmt(x.avg_ticket)}</td>
              <td className="text-right">{fmt(x.by_method.cash || 0)}</td><td className="text-right pr-4">{fmt(x.by_method.card || 0)}</td>
              <td className="pr-4 text-slate-600">{x.best_day ? x.best_day.slice(8) + "/" + x.best_day.slice(5, 7) : "—"}</td>
              <td className="text-xs text-slate-600">{x.top_items.slice(0, 3).map((i) => `${i.name} ×${i.qty}`).join(", ") || "—"}</td>
            </tr>))}</tbody>
        </table></div>
      </Card>
      <div className="mt-6"><MarginsCard rid={restaurant?.id} month={month} /></div>
    </div>
  );
}
